import { APP_LOCALE, EMPTY_VALUE } from './dates';

/**
 * Money and counts, in pt-BR conventions: `R$ 1.234,56`, not `$1,234.56`.
 *
 * Amounts arrive from the API as decimal strings, not floats, because a premium
 * is money and `0.1 + 0.2` is not `0.3`. They are formatted, never arithmetic'd,
 * here.
 */

const currencyFormatter = new Intl.NumberFormat(APP_LOCALE, {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const integerFormatter = new Intl.NumberFormat(APP_LOCALE, { maximumFractionDigits: 0 });

const decimalFormatter = new Intl.NumberFormat(APP_LOCALE, {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Intl puts a no-break space after `R$`. Normalised so the output is predictable. */
function normaliseSpaces(value: string): string {
  return value.replace(/[\u00A0\u202F]/g, ' ');
}

function toNumber(value: string | number | null | undefined): number | undefined {
  if (value === null || value === undefined || value === '') {
    return undefined;
  }
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

/** `1234.5` becomes `R$ 1.234,50`. */
export function formatCurrency(value: string | number | null | undefined): string {
  const amount = toNumber(value);
  return amount === undefined ? EMPTY_VALUE : normaliseSpaces(currencyFormatter.format(amount));
}

/**
 * `195301.8` becomes `195.301,80`: an amount without the `R$`, the way an
 * insurer's own file writes its capital and premium.
 */
export function formatDecimal(value: string | number | null | undefined): string {
  const amount = toNumber(value);
  return amount === undefined ? EMPTY_VALUE : decimalFormatter.format(amount);
}

/** `1234` becomes `1.234`. For counts, never for money. */
export function formatInteger(value: number | null | undefined): string {
  return value === null || value === undefined || !Number.isFinite(value)
    ? EMPTY_VALUE
    : integerFormatter.format(value);
}

/**
 * `4.250,00` back to the `4250.00` the API takes, or undefined if it is not an
 * amount.
 *
 * Parsing is here, beside formatting, for the reason the module above it gives:
 * this is locale-sensitive, and a screen that grew its own parser would be a
 * screen where `1.500` silently became one and a half reais.
 *
 * Both spellings are accepted, because both get typed. A comma is unambiguous
 * -- it is the decimal separator in pt-BR, so dots beside it are thousands
 * separators. Without a comma, a single dot with one or two digits after it is
 * a decimal point (`4250.00`, pasted out of a payroll system), and anything
 * else is a thousands separator (`4.250`). The amount is returned as a string
 * and never as a number: money goes to the API as an exact decimal.
 */
export function parseBrazilianAmount(value: string): string | undefined {
  const cleaned = value.replace(/\s|R\$/g, '').trim();
  if (cleaned === '') {
    return undefined;
  }

  const normalised = cleaned.includes(',')
    ? cleaned.replace(/\./g, '').replace(',', '.')
    : /^\d+\.\d{1,2}$/.test(cleaned)
      ? cleaned
      : cleaned.replace(/\./g, '');

  return /^\d+(\.\d{1,2})?$/.test(normalised) ? normalised : undefined;
}
